/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file dashboardCardCache.js
 * @description
 * Tiny in-memory TTL cache for the management dashboard's AI / intelligence cards. Those cards run
 * a `useFocusEffect` fetch of several aggregate endpoints (stock forecast, margins, VAT check,
 * cashier anomalies, supplier prices, digest, goals…) — each one a full-catalog scan on the server.
 * Without a cache every return to the dashboard (tab switch, back from a detail screen) re-ran all of
 * them; on a store with an 80k-product catalog that was the dominant cost of "just opening Home".
 *
 * Entries are keyed per card + scope (+ locale where relevant) and live for {@link DASHBOARD_CARD_TTL_MS};
 * pull-to-refresh style callers can bypass with {@link clearDashboardCardCache}.
 *
 * @module helpers/dashboardCardCache
 */

/** How long a card payload is considered fresh. */
export const DASHBOARD_CARD_TTL_MS = 2 * 60 * 1000;

const store = new Map();

/**
 * Build a stable cache key from a card id and the active management scope.
 *
 * @param {string} card card identifier.
 * @param {{restaurantId?: string, placeId?: string}|null} [scope] active management scope.
 * @param {...(string|number|boolean|null|undefined)} extra extra discriminators (locale, gating flags…).
 * @returns {string} the cache key.
 */
export const dashboardCardKey = (card, scope, ...extra) => (
    [card, scope?.restaurantId || '', scope?.placeId || '', ...extra.map((part) => String(part ?? ''))].join('|')
);

/**
 * Read a still-fresh payload.
 *
 * @param {string} key cache key from {@link dashboardCardKey}.
 * @param {number} [ttlMs] freshness window.
 * @returns {*} the cached payload, or `undefined` when missing / stale.
 */
export const readDashboardCard = (key, ttlMs = DASHBOARD_CARD_TTL_MS) => {
    const entry = store.get(key);

    if (!entry || (Date.now() - entry.at) > ttlMs) return undefined;

    return entry.value;
};

/**
 * Store a payload.
 *
 * @param {string} key cache key from {@link dashboardCardKey}.
 * @param {*} value payload to keep.
 * @returns {void}
 */
export const writeDashboardCard = (key, value) => {
    store.set(key, {at: Date.now(), value});
};

/**
 * Drop every cached card (e.g. on an explicit user refresh or a store switch).
 *
 * @returns {void}
 */
export const clearDashboardCardCache = () => {
    store.clear();
};
